import { useMemo, useState } from 'react';

type Validation = (value: any) => string | void;
type Filter = (value: any) => string;

interface RegisterParams {
  /** Набор функций-валидаторов. Если валидация не пройдена, то подсвечивает поле и показывает сообщение с ошибкой */
  validations?: Validation[];

  /** Функции-фильтры. Позволяют изменить значение value перед его установкой */
  filters?: Filter[];
}

interface SetFormValueParams {
  /** Поле было изменено путем изменения значения в самом поле. Запускает работу валидации на поле. */
  isChanged?: boolean;
}

interface FormValue {
  /** Текущее значение поля формы */
  value: any;

  /** Сообщение с ошибкой валидации */
  validationFailureMessage?: string;

  /** Набор функций-валидаторов */
  validations?: Validation[];

  /** Поле было изменено путем изменения значения в самом поле. Запускает работу валидации на поле. */
  isChanged?: boolean;

  /** Функции-фильтры. Позволяют изменить значение value перед его установкой */
  filters?: Filter[];
}

/** Удалить все символы кроме цифр */
export const numbersOnlyFilter = (value: string = '') => {
  return value?.replace(/[\D]+/g, '');
}

/**
 * Хук для более удобной работы с формами с валидацией.
 * Также позволяет изменять значения формы перед установкой с помощью filters.
 *
 * Использование сводится к 4ем шагам:
 * 1. Установить стартовое значение через useFormHook(startObject)
 * 2. Зарегистрировать поле через registerField()
 * 3. Установить отлов изменений поля через handleFormChange()
 * 4. Использовать метод validate() для валидации всей формы
 *
 * @param defaultForm - значение формы по умолчанию.
 */
const useFormHook = (defaultForm?: Record<string, any>) => {
  const [form, setForm] = useState(getPreparedDefaultForm(defaultForm) as Record<string, FormValue>);
  /** Была ли использована функция validate() */
  const [isValidated, setIsValidated] = useState(false);

  function getPreparedDefaultForm(defaultForm: Record<string, any> = {}) {
    const preparedForm = {} as Record<string, FormValue>;

    Object.entries(defaultForm).forEach(([key, value]) => {
      preparedForm[key] = {
        value,
        filters: [],
        validations: [],
        validationFailureMessage: '',
        isChanged: false,
      };
    });

    return preparedForm;
  }

  /**
   * Зарегистрировать поле для управления через хук
   *
   * @param key - ключ поля формы
   * @param params - объект с различными настройками поля
   *
   * @return объект с ошибкой, если есть ошибка валидации
   *
   * @example
   * registerField('email', {validations: [...]})
   */
  const registerField = (key: string, params: RegisterParams = {}) => {
    if (!form[key]) {
      setForm({
        ...form,
        [key]: {
          value: undefined,
          filters: params.filters,
          validationFailureMessage: '',
          validations: params.validations,
          isChanged: false,
        },
      });

      return {};
    }

    // Условие для корректной работы validationIf
    if (
      applyValidation(form[key].value, params.validations) !==
      applyValidation(form[key].value, form[key].validations)
    ) {
      setForm({
        ...form,
        [key]: {
          ...form[key],
          validations: params.validations,
          filters: params.filters,
          validationFailureMessage:
            isValidated || form[key].isChanged
              ? applyValidation(form[key].value, params.validations || [])
              : '',
        },
      });
    }

    return {
      validationFailureMessage: form[key].validationFailureMessage,
    };
  };

  /**
   * Применить валидаторы к значению
   *
   * @returns текст ошибки, если не была пройдена какая-то из валидаций
   */
  const applyValidation = (value: any, validations: Validation[] = []) => {
    let message = '';

    for (const validation of validations) {
      message = validation(value) || '';

      if (message) {
        break;
      }
    }

    return message;
  };

  /** Обновить значения формы */
  const setFormValue = (
    newValue: Record<string, any>,
    params: SetFormValueParams = {},
  ) => {
    const newForm = {} as Record<string, FormValue>;

    Object.entries(newValue).forEach(([key, value]) => {
      let computedValue = value;

      // Применить фильтры, если они есть
      if (form[key]?.filters?.length) {
        form[key].filters.forEach(filter => {
          computedValue = filter(computedValue);
        })
      }

      newForm[key] = {
        ...form[key],
        ...params,
        value: computedValue,
        isChanged: params.isChanged || false,
        validationFailureMessage: '',
      };
    });

    setForm(prevForm => {
      return {
        ...prevForm,
        ...newForm,
      };
    });
  };

  /**
   * Обновить значения формы и пометить переданные поля как "измененные" для
   * срабатывания точечной валидации поля
   */
  const handleFormChange = (newValue: Record<string, any>, newParams?: Record<string, any>) => {
    setFormValue(newValue, { ...newParams, isChanged: true });
  };

  /**
   * Произвести валидацию всех полей формы.
   *
   * @returns true, если форма не содержит ошибок валидации.
   */
  const validate = (): boolean => {
    const newForm = {} as Record<string, FormValue>;
    let isValid = true;

    Object.entries(form).forEach(([key, value]) => {
      const validationFailureMessage = applyValidation(value.value, value?.validations);

      if (validationFailureMessage) {
        isValid = false;
      }

      newForm[key] = { ...value, validationFailureMessage };
    });

    setForm(prevForm => ({
      ...prevForm,
      ...newForm,
    }));

    setIsValidated(true);

    return isValid;
  };

  /**
   * Флаг, указывающий на валидность формы
   *
   * @returns true, если форма валидна.
   */
  const isValidForm = useMemo(() => {
    return !Object.values(form).some(value => !!value.validationFailureMessage);
  }, [form]);

  /** Упрощенная форма вида {ключ:значение} */
  const flatForm = useMemo(() => {
    return Object.entries(form).reduce((acc, [key, value]) => {
      acc[key] = value.value;

      return acc;
    }, {} as Record<string, any>);
  }, [form]);

  return {
    form: flatForm,
    registerField,
    setFormValue,
    isValidForm,
    validate,
    handleFormChange,
  };
};

export default useFormHook;
